import { Check, FlipBackward } from '@untitledui/icons'
import { twJoin } from 'tailwind-merge'

import { ProductBarcodeViewTriggerBadge, ProductCard } from '@components/ProductCard'
import Button from '@components/designSystem/Button'
import { Badge } from '@components/designSystem/badges/badges'
import useCanWriteTasks from '@hooks/useCanWriteTasks.ts'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { CheckBadge, CheckedBadge, RefillBadge, RefilledBadge, ReorderBadge, ReorderedBadge } from '@routes/TodoList/badges'
import { TodoItemWrapper } from '@state/util/todoList'

import type { ProductWrapper } from '@state/product.ts'
import type { ACTION_TYPE } from '@state/todoList.ts'
import type { JSX } from 'react'

interface ProductCardTodoListProps {
  item: TodoItemWrapper
  isSelected?: boolean
  onSelect: () => void
  onComplete: () => void
  onUndo: () => void
}

function ProductCardTodoList({ item, onSelect, isSelected = false, ...props }: ProductCardTodoListProps) {
  const { product } = item

  const utilities: JSX.Element[] = []

  if (isSelected && item.action !== 'NONE') {
    const actionIndicatorClasses = twJoin(
      'absolute inset-y-0 left-0 w-1',
      getColorByTaskAction(item.action)
    )
    utilities.push(<div className={actionIndicatorClasses} key='indicator' />)
  }

  return (
    <ProductCard
      product={product}
      onClick={onSelect}
      isSelected={isSelected}
      badges={[
        <ActionBadge key='action' item={item} />,
        <StockBadge key='stock' product={item.product} />,
        <ProductBarcodeViewTriggerBadge key='barcode' product={product} />,
        item.isNew && <NewBadge item={item} key='new-badge' />,
      ].filter(Boolean) as JSX.Element[]}
      utilities={utilities}
      footer={<ActionFooter item={item} isSelected={isSelected} {...props} />}
    />
  )
}

type ActionFooterProps = Pick<ProductCardTodoListProps, 'item'| 'isSelected' | 'onComplete' | 'onUndo'>

function ActionFooter({ item, isSelected, onComplete, onUndo }: ActionFooterProps) {
  const { t } = useTypedTranslation()
  const canWriteTasks = useCanWriteTasks()

  if (!canWriteTasks) return null

  let label
  let icon
  let action

  if (item.isCompleted()) {
    label = t('todoList.undo')
    icon = FlipBackward
    action = onUndo
  } else {
    switch (item.action) {
      case 'CHECK': label = t('todoList.badges.CHECKED');break
      case 'REFILL': label = t('todoList.badges.REFILLED');break
      case 'REORDER': label = t('todoList.badges.REORDER');break
      default: label = null
    }
    icon = Check
    action = onComplete
  }

  return (
    <div
      className={
        twJoin(
          'flex flex-col items-center overflow-hidden transition-all ease-in-out',
          isSelected ? 'max-h-24 duration-300' : 'max-h-0 duration-150'
        )
      }
      aria-hidden={!isSelected ? true : undefined}
    >
      <div className={
        twJoin(
          'flex w-full flex-col items-center pt-3 transition-opacity ease-in-out',
          isSelected ? 'opacity-100 delay-300 duration-300' : 'opacity-0 delay-0 duration-100'
        )
      }>
        <div className='h-px w-11/12 bg-border-secondary' />
        <Button
          className='mt-2.5 mb-1 w-1/2 text-xs'
          iconLeading={icon}
          size='sm'
          onClick={action}
        >
          {label}
        </Button>
      </div>
    </div>
  )
}

interface NewBadgeProps {
  item: TodoItemWrapper
}

function NewBadge({ item }: NewBadgeProps) {
  const { t } = useTypedTranslation()

  const color = getColorByTaskAction(item.action)

  if (!color) return null

  return (
    <div className={`
      flex items-center rounded-full
      ${color}
      px-2 text-xs text-[white]
    `} data-testid='new-badge'>
      {t('todoList.badges.NEW')}
    </div>
  )
}

interface ActionBadgeProps {
  item: TodoItemWrapper
}

function ActionBadge({ item }: ActionBadgeProps) {
  //TODO color of badge based on action
  if (item.isCompleted()) {
    if (item.action === 'CHECK') {
      return <CheckedBadge />
    } else if (item.action === 'REFILL') {
      return <RefilledBadge />
    } else if (item.action === 'REORDER') {
      return <ReorderedBadge />
    }
  } else {
    if (item.action === 'REFILL') {
      return <RefillBadge />
    } else if (item.action === 'CHECK') {
      return <CheckBadge />
    } else if (item.action === 'REORDER') {
      return <ReorderBadge />
    }
  }

  return null
}

interface StockBadgeProps {
  product: ProductWrapper
}

function StockBadge({ product }: StockBadgeProps) {
  const { t } = useTypedTranslation()

  if (product.hasPositiveInventory() === true) {
    return <Badge size='sm' key='badge-positive'>{t('todoList.badges.nInStock', { n: product.inventory?.quantity })}</Badge>
  } else if (product.hasPositiveInventory() === false) {
    return <Badge color='error' size='sm' key='badge-negative'>{t('todoList.badges.nInStock', { n: 0 })}</Badge>
  }

  return null
}

function getColorByTaskAction(action: ACTION_TYPE) {
  switch (action) {
    case 'REFILL':
      return 'bg-todo-action-refill'
    case 'CHECK':
      return 'bg-todo-action-check'
    case 'REORDER':
      return 'bg-todo-action-reorder'
    default:
      return ''
  }
}

export default ProductCardTodoList